import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "cn"
import { noticeSurface } from "@/components/ui/notice-surface"

// CUSTOMIZED: Soft semantic severity variants (`success` / `warning` /
// `destructive`) using shared `noticeSurface` tones — accent on border + icon,
// body stays muted. `default` stays the registry plain card. Toast is the
// overlay host for the same surface (see toast.tsx). Color system notes live
// in index.css.

const alertVariants = cva(
  "group/alert relative grid w-full gap-0.5 rounded-lg border px-4 py-3 text-left text-sm has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2.5 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Registry: plain card; severity only if the copy/icon carry it.
        default: noticeSurface.default,
        // Soft semantic recipe: shared surface + host icon tint. Icons are
        // decorative; text contrast is the floor (~5% tint).
        destructive: cn(noticeSurface.destructive, "*:[svg]:text-destructive"),
        success: cn(noticeSurface.success, "*:[svg]:text-success"),
        warning: cn(noticeSurface.warning, "*:[svg]:text-warning"),
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground",
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-sm text-balance text-muted-foreground md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4",
        className
      )}
      {...props}
    />
  )
}

function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-action"
      className={cn("absolute top-2.5 right-3", className)}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription, AlertAction }
